import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MessageCircle, Plus, Minus, Palette, Ruler, Layers, X } from "lucide-react";
import PageLayout from "@/components/PageLayout";
import SEO from "@/components/SEO";
import { StockBadge, StockLegend } from "@/components/StockBadge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "917624853840";

const SPEC_ROWS = [
  { key: "logo", label: "Brand Seal", icon: null, render: (p) => p.logo_url ? (
      <img
        src={`${process.env.REACT_APP_BACKEND_URL}${p.logo_url}`}
        alt={`${p.name} seal`}
        className="h-24 w-24 lg:h-32 lg:w-32 object-contain mx-auto"
      />
    ) : "—" },
  { key: "stock_status", label: "Stock Availability", icon: null,
    render: (p) => <StockBadge status={p.stock_status || "ready"} /> },
  { key: "name", label: "Product", icon: Layers, render: (p) => (
      <span className="font-medium" style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)", fontSize: "20px" }}>
        {p.name}
      </span>
    ) },
  { key: "panna", label: "Panna", icon: Ruler, render: (p) => p.panna },
  { key: "width", label: "Width", icon: Ruler, render: (p) => p.width },
  { key: "material", label: "Material", icon: Layers, render: (p) => p.material },
  { key: "dye_type", label: "Dye Type", icon: null, render: (p) => p.dye_type },
  { key: "fold", label: "Fold", icon: null, render: (p) => p.fold },
  { key: "cut_pack", label: "Cut Pack", icon: null, render: (p) => p.cut_pack },
  { key: "colors_available", label: "Shades Available", icon: Palette, render: (p) => `${p.colors_available} colours` },
];

export default function ComparePage() {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    fetch(`${API}/products`)
      .then((r) => r.json())
      .then(setProducts)
      .catch((e) => console.error("Compare products failed", e));
  }, []);

  const addSlot = (id) => {
    if (!id || selected.includes(id)) return;
    if (selected.length >= 4) return;
    setSelected([...selected, id]);
  };

  const removeSlot = (id) => setSelected(selected.filter((s) => s !== id));

  const chosen = selected
    .map((id) => products.find((p) => p.id === id))
    .filter(Boolean);

  const sendCompareToWhatsApp = () => {
    if (!chosen.length) return;
    const lines = chosen.map(
      (p) =>
        `• ${p.name} — ${p.panna}, ${p.width}, ${p.material}, ${p.colors_available} shades`
    );
    const text = `Hi Mutha Paras & Co.,\n\nI'm comparing the following qualities and would like a quote / advice on which suits my need best:\n\n${lines.join(
      "\n"
    )}\n\nPlease share latest prices & availability.`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <PageLayout>
      <SEO
        title="Compare Fabric Qualities — 600 vs 600 Gold, 060 vs 006 & More | Mutha Paras & Co."
        description="Compare up to 4 lining fabric qualities side-by-side. See panna, width, dye type, colours available and fold-pack — then send the comparison to us on WhatsApp for a quote."
        path="/compare"
      />
      <main
        className="py-14 lg:py-20"
        style={{ backgroundColor: "var(--mp-paper)" }}
        data-testid="compare-page"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Link to="/products" className="inline-flex items-center gap-2 text-sm mb-6" style={{ color: "var(--mp-text-strong)", fontFamily: "'Manrope', sans-serif" }}>
            <ArrowLeft className="w-4 h-4" /> Back to all products
          </Link>

          <div className="text-center mb-10">
            <p
              className="text-[10px] uppercase tracking-[0.28em] font-semibold mb-3"
              style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-copper)" }}
            >
              Side-by-side
            </p>
            <h1
              className="text-3xl md:text-4xl font-medium tracking-tight mb-3"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
              data-testid="compare-heading"
            >
              Compare Fabric Qualities
            </h1>
            <p className="text-sm md:text-base" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
              Pick up to 4 qualities and see the full spec grid — then send the comparison to us on WhatsApp for a live quote.
            </p>
          </div>

          {/* Slot chips */}
          <div className="flex flex-wrap items-center gap-3 mb-8 justify-center">
            {chosen.map((p) => (
              <div
                key={p.id}
                className="flex items-center gap-2 px-3 py-2 rounded-full bg-white border border-[var(--mp-line)]"
                data-testid={`compare-slot-${p.id}`}
              >
                <span className="text-xs font-medium" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}>
                  {p.name}
                </span>
                <button
                  onClick={() => removeSlot(p.id)}
                  className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-red-50"
                  aria-label={`Remove ${p.name}`}
                >
                  <X className="w-3 h-3" style={{ color: "var(--mp-copper)" }} />
                </button>
              </div>
            ))}
            {selected.length < 4 && (
              <Select onValueChange={addSlot}>
                <SelectTrigger className="w-64 rounded-full border-[var(--mp-line)]" data-testid="compare-add-select">
                  <SelectValue placeholder="+ Add a quality to compare" />
                </SelectTrigger>
                <SelectContent>
                  {products.filter((p) => !selected.includes(p.id)).map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {chosen.length === 0 && (
            <div className="text-center py-16 border border-dashed border-[var(--mp-line)] rounded-sm bg-white">
              <Plus className="w-8 h-8 mx-auto mb-3" style={{ color: "var(--mp-copper)" }} />
              <p className="text-sm" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
                Pick 2–4 qualities from the dropdown above to see the comparison grid.
              </p>
            </div>
          )}

          {chosen.length > 0 && (
            <div
              className="overflow-x-auto rounded-sm bg-white border border-[var(--mp-line)]"
              data-testid="compare-table-wrapper"
            >
              <table className="w-full text-sm" data-testid="compare-table">
                <thead>
                  <tr>
                    <th className="text-left p-4 border-b border-[var(--mp-line)] w-40"
                      style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
                    >Spec</th>
                    {chosen.map((p) => (
                      <th key={p.id} className="p-4 border-b border-l border-[var(--mp-line)] min-w-[180px]">
                        <Link
                          to={`/product/${p.id}`}
                          className="text-sm font-semibold hover:underline"
                          style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text-strong)" }}
                        >
                          {p.name}
                        </Link>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SPEC_ROWS.map((row, ri) => (
                    <tr key={row.key} className={ri % 2 === 0 ? "bg-white" : "bg-[var(--mp-paper)]"}>
                      <td className="p-4 text-xs uppercase tracking-wider font-semibold"
                        style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
                      >{row.label}</td>
                      {chosen.map((p) => (
                        <td key={p.id} className="p-4 border-l border-[var(--mp-line)] text-center align-middle"
                          style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}
                        >
                          {row.render(p)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {chosen.length > 0 && <StockLegend className="mt-6" />}

          {chosen.length >= 2 && (
            <div className="mt-8 text-center">
              <Button
                onClick={sendCompareToWhatsApp}
                className="text-white text-sm font-medium px-8 py-6 rounded-sm"
                style={{ backgroundColor: "#25D366", fontFamily: "'Manrope', sans-serif" }}
                data-testid="compare-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Send comparison to WhatsApp for a quote
              </Button>
            </div>
          )}
        </div>
      </main>
    </PageLayout>
  );
}
